// src/components/SubscriptionBanner.tsx
// Banner que muestra el estado de la suscripción (trial, expirado, etc.)

import React from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { Sparkles, AlertTriangle } from 'lucide-react';

interface SubscriptionBannerProps {
  onSubscribeClick: () => void;
}

// Días antes del final en que aparece el aviso de la prueba.
//
// Oct-2026 (Esteban): antes el cartel "Prueba Gratuita · 15 días restantes ·
// Suscribirse" ocupaba la primera pantalla del panel desde el primer día, a
// alguien que todavía no había visto nada de la app. Ahora la prueba no se
// nombra hasta que se está por terminar.
const DIAS_DE_AVISO = 3;

// "hoy" / "mañana" / "en 3 días", por fecha de calendario y no por horas: con
// 5 horas por delante la cuenta redondeada decía "1 día" cuando es hoy.
const cuandoTermina = (vence: Date | null, diasRestantes: number) => {
  if (!vence) return `en ${diasRestantes} días`;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const dia = new Date(vence);
  dia.setHours(0, 0, 0, 0);
  const dias = Math.round((dia.getTime() - hoy.getTime()) / 86400000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'mañana';
  return `en ${dias} días`;
};

const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({ onSubscribeClick }) => {
  const { isTrial, isExpired, daysRemaining, expiresAt, subscriptionData, loading } = useSubscription();

  // No mostrar nada si está cargando o si tiene suscripción activa
  if (loading || (!isTrial && !isExpired)) {
    return null;
  }

  // Prueba en curso: solo en los últimos días
  if (isTrial && daysRemaining > 0) {
    if (daysRemaining > DIAS_DE_AVISO) return null;
    // El precio de la cuenta (las nuevas, $20.000; las viejas conservan el suyo)
    const precio = Number((subscriptionData as any)?.monthly_price);
    return (
      <div className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-lg border border-teal-400/30 rounded-xl p-3 mb-4 relative z-30 mx-4 mt-2 text-left">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center space-x-2.5 min-w-0">
            <Sparkles className="w-5 h-5 flex-shrink-0 text-yellow-300" />
            <div className="min-w-0">
              <h3 className="font-bold text-sm leading-tight">
                Tu prueba gratis termina {cuandoTermina(expiresAt, daysRemaining)}
              </h3>
              <p className="text-xs text-teal-50 mt-0.5">
                Suscribite para seguir con tu agenda y tu página
                {precio > 0 ? ` · $${precio.toLocaleString('es-AR')} por mes` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onSubscribeClick}
            className="flex-shrink-0 bg-white text-teal-600 px-4 py-1.5 rounded-lg font-bold text-xs hover:bg-teal-50 transition-all shadow-sm active:scale-95"
          >
            Suscribirme
          </button>
        </div>
      </div>
    );
  }

  // Banner para trial expirado
  if (isExpired || (isTrial && daysRemaining === 0)) {
    return (
      <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg border border-red-400/50 rounded-xl p-3 mb-4 relative z-30 mx-4 mt-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 text-yellow-200" />
            <div>
              <h3 className="font-bold text-sm leading-tight">
                Prueba Expirada
              </h3>
              <p className="text-xs text-red-100 mt-0.5 leading-tight">
                Recupera tu acceso
              </p>
            </div>
          </div>
          <button
            onClick={onSubscribeClick}
            className="bg-white text-red-600 px-4 py-1.5 rounded-lg font-bold text-xs hover:bg-red-50 transition-all shadow-sm active:scale-95 animate-pulse"
          >
            Suscribirse
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default SubscriptionBanner;